// Templates aprovados pela Meta — o que permite falar com quem não escreve há
// mais de 24 horas.
//
// A régua é a mesma do resto do trilho oficial: o cliente NUNCA precisa abrir o
// WhatsApp Manager. Ele descreve cabeçalho, corpo, rodapé e botões aqui, e a
// API monta o formato de componentes da Meta — com exemplos aninhados em três
// níveis e regras que ela não documenta junto.
//
// A validação acontece ANTES de submeter e é o coração desta tela: a Meta leva
// até 24 horas para responder, rejeita com um motivo lacônico e limita quantas
// submissões se pode fazer. O erro que aparece aqui ENSINA o que consertar.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BookOpen, Check, CircleAlert, Clock, FileText, Loader2, MapPin, Pencil, Plus, RefreshCw, Send, Star, Trash2, TriangleAlert, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/ui/misc';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useProjects } from '@/components/ProjectSwitcher';
import { TemplateRoles, type TemplatePreset } from '@/components/TemplateRoles';
import { LibraryDialog } from '@/components/TemplateLibrary';
import { TestSendDialog } from '@/components/TemplateTestSend';
import { CarouselEditor, CarouselPreview, FormatPicker, OfferFields } from '@/components/TemplateRichFields';
import {
  buildCards, cardsFromSpec, emptyCard,
  type CardButtonShape, type CardDraft, type CardMedia, type OfferDraft, type SpecCard, type TemplateFormat,
} from '@/lib/template-rich';
import { otpReleased, useOTPInfo } from '@/lib/otp';
import { useTemplateLiveUpdates } from '@/lib/template-live';
import { apiFetch, getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { useT } from '@/lib/i18n';

const taClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

type Status = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';
type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

interface TemplateButton {
  type: 'quick_reply' | 'url' | 'phone_number' | 'copy_code';
  text: string;
  url?: string;
  phone?: string;
  example?: string;
}

interface Spec {
  name: string;
  language: string;
  category: Category;
  header?: { type: string; text?: string; example?: string; media_url?: string };
  // buttons já existia no backend e nunca foi exposto na tela.
  body: { text: string; example?: string[] };
  footer?: string;
  buttons?: TemplateButton[];
  code_expiry_minutes?: number;
  limited_time_offer?: OfferDraft;
  cards?: SpecCard[];
}

interface Template {
  id: string;
  name: string;
  language: string;
  category: Category;
  status: Status;
  rejected_reason?: string;
  quality?: string;
  variables: number;
  spec: Spec;
  created_at?: string;
}

const statusVariant = (s: Status) =>
  s === 'APPROVED' ? 'success' : s === 'PENDING' ? 'warning' : s === 'REJECTED' ? 'destructive' : 'secondary';

/** Conta as variáveis {{n}} distintas de um texto (espelha a regra do servidor). */
/**
 * Variáveis do texto, na ORDEM em que aparecem, aceitando as duas formas da
 * Meta: numeradas ({{1}}) e nomeadas ({{nome_do_cliente}}).
 *
 * A ordem importa porque é ela que alinha cada campo de exemplo à sua variável.
 * E o formato não é escolha de menu: sai do que a pessoa escreveu — o servidor
 * detecta igual e recusa a mistura dos dois.
 */
function parseVars(text: string): { names: string[]; named: boolean; mixed: boolean } {
  const names: string[] = [];
  const seen = new Set<string>();
  let hasNum = false;
  let hasNamed = false;
  for (const m of text.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)) {
    const v = m[1];
    if (/^\d+$/.test(v)) hasNum = true;
    else if (/^[a-z][a-z0-9_]*$/.test(v)) hasNamed = true;
    else continue;
    if (!seen.has(v)) { seen.add(v); names.push(v); }
  }
  return { names, named: hasNamed && !hasNum, mixed: hasNum && hasNamed };
}

export function Templates() {
  const { data: projects = [] } = useProjects();
  const active = projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default);

  // Num projeto não oficial não existe template aprovado: não há aprovação
  // prévia no trilho. Dizer isso é mais útil que esconder o menu.
  if (projects.length > 0 && !isOfficial(active)) return <NotOfficial />;
  return <TemplateList />;
}

function NotOfficial() {
  const t = useT();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
        <CircleAlert className="size-8 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t('templates.notOfficial.title')}</h2>
        <p className="max-w-md text-sm text-muted-foreground">{t('templates.notOfficial.body')}</p>
      </CardContent>
    </Card>
  );
}

function TemplateList() {
  const t = useT();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  // Modelo pronto vindo das funções (alternativa, código): abre o formulário já
  // preenchido dentro das regras da Meta.
  const [preset, setPreset] = useState<TemplatePreset | null>(null);
  // Template em edição: o mesmo formulário, preenchido, com nome e idioma fixos.
  const [editing, setEditing] = useState<Template | null>(null);
  const [library, setLibrary] = useState(false);
  const [testing, setTesting] = useState<Template | null>(null);
  // Decisões da Meta ao vivo (aprovado, recusado, pausado, reclassificado).
  useTemplateLiveUpdates();
  // Alvo da remoção: guarda o template inteiro, não só o id — o diálogo mostra
  // nome, idioma e status para o cliente conferir ANTES, não depois.
  const [pendingDelete, setPendingDelete] = useState<Template | null>(null);

  const { data: list, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async (): Promise<Template[]> =>
      ((await apiFetch('GET', '/templates')).data as { templates?: Template[] })?.templates ?? [],
  });

  const { data: fallback } = useQuery({
    queryKey: ['templates', 'fallback'],
    queryFn: async (): Promise<Template | null> =>
      ((await apiFetch('GET', '/templates/fallback')).data as { template?: Template | null })?.template ?? null,
  });

  // Desempenho dos últimos 30 dias, por nome de template (o que o envio grava).
  const { data: stats } = useQuery({
    queryKey: ['templates', 'analytics'],
    queryFn: async (): Promise<Record<string, TemplateStats>> => {
      const r = await apiFetch('GET', '/templates/analytics?days=30');
      const list = r.ok ? ((r.data as { templates?: TemplateStats[] })?.templates ?? []) : [];
      return Object.fromEntries(list.map((x) => [x.name, x]));
    },
  });

  const sync = useMutation({
    mutationFn: async () => {
      const r = await apiFetch('POST', '/templates/sync');
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.syncFailed'));
      return r.data as { imported: number };
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success(t('templates.synced', { n: d.imported }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setFallback = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch('PUT', '/templates/fallback', { template_id: id });
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.fallbackFailed'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast.success(t('templates.fallbackSaved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch('DELETE', `/templates/${id}`);
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.deleteFailed'));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setPendingDelete(null);
      toast.success(t('templates.deleted'));
    },
    // Falhou: o diálogo FICA aberto. Fechá-lo esconderia o motivo e o cliente
    // tentaria de novo às cegas.
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('templates.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('templates.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
            {sync.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            {t('templates.sync')}
          </Button>
          <Button variant="outline" onClick={() => setLibrary(true)}>
            <BookOpen className="size-4" />{t('templates.library.open')}
          </Button>
          <Button onClick={() => setCreating(true)}><Plus className="size-4" />{t('templates.new')}</Button>
        </div>
      </div>

      {(list ?? []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(['APPROVED', 'PENDING', 'REJECTED', 'PAUSED'] as const).map((st) => {
            const n = (list ?? []).filter((x) => x.status === st || (st === 'PAUSED' && x.status === 'DISABLED')).length;
            return (
              <span key={st} className={'rounded-md border px-3 py-1.5 text-sm ' + (n === 0 ? 'text-muted-foreground' : '')}>
                <strong className="mr-1.5 tabular-nums">{n}</strong>{t(`templates.status.${st}`)}
              </span>
            );
          })}
        </div>
      )}

      <TemplateRoles
        templates={list ?? []}
        fallback={fallback ?? null}
        onCreate={(p) => { setPreset(p); setCreating(true); }}
      />

      {(list ?? []).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <h2 className="text-lg font-semibold">{t('templates.empty.title')}</h2>
            <p className="max-w-md text-sm text-muted-foreground">{t('templates.empty.body')}</p>
            <Button onClick={() => setCreating(true)}><Plus className="size-4" />{t('templates.new')}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {(list ?? []).map((tpl) => (
            <TemplateRow
              key={tpl.id}
              tpl={tpl}
              isFallback={fallback?.id === tpl.id}
              onFallback={() => setFallback.mutate(tpl.id)}
              onDelete={() => setPendingDelete(tpl)}
              onEdit={() => { setEditing(tpl); setCreating(true); }}
              onTest={() => setTesting(tpl)}
              stats={stats?.[tpl.name]}
            />
          ))}
        </div>
      )}

      {/* Remover é irreversível dos DOIS lados — some daqui e da Meta —, e
          recriar custa uma análise nova de até 24h. Pede confirmação, mostra o
          nome do alvo, e avisa quando o alvo é a alternativa do projeto. */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title={t('templates.confirmTitle')}
        description={t('templates.confirmBody')}
        confirmLabel={t('templates.delete')}
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      >
        {pendingDelete && (
          <div className="flex flex-col gap-2">
            <div className="rounded-md border bg-muted/40 px-3 py-2">
              <p className="font-mono text-sm font-medium">{pendingDelete.name}</p>
              <p className="text-xs text-muted-foreground">
                {pendingDelete.language} · {pendingDelete.category} · {pendingDelete.status}
              </p>
            </div>
            {fallback?.id === pendingDelete.id && (
              <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
                {t('templates.confirmInUse')}
              </p>
            )}
          </div>
        )}
      </ConfirmDialog>

      <CreateDialog open={creating} onOpenChange={(v) => { setCreating(v); if (!v) { setPreset(null); setEditing(null); } }}
        preset={preset} editing={editing} />
      <LibraryDialog open={library} onOpenChange={setLibrary} />
      <TestSendDialog tpl={testing} onOpenChange={(v) => { if (!v) setTesting(null); }} />
    </div>
  );
}

interface TemplateStats { name: string; sent: number; delivered: number; read: number; failed: number; delivery_rate: number; read_rate: number }

function TemplateRow({
  tpl, isFallback, onFallback, onDelete, onEdit, onTest, stats,
}: {
  tpl: Template; isFallback: boolean; onFallback: () => void; onDelete: () => void; onEdit: () => void;
  onTest: () => void; stats?: TemplateStats;
}) {
  const t = useT();
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{tpl.name}</span>
          <Badge variant="secondary">{tpl.language}</Badge>
          <Badge variant="outline">{t(`templates.category.${tpl.category}`)}</Badge>
          {!!tpl.spec?.cards?.length && (
            <Badge variant="secondary">{t('templates.rich.formats.carousel')} · {tpl.spec.cards.length}</Badge>
          )}
          {tpl.spec?.limited_time_offer && (
            <Badge variant="secondary">⏱ {tpl.spec.limited_time_offer.text || t('templates.rich.formats.offer')}</Badge>
          )}
          <Badge variant={statusVariant(tpl.status)} className="gap-1">
            {tpl.status === 'APPROVED' && <Check className="size-3" />}
            {tpl.status === 'PENDING' && <Clock className="size-3" />}
            {tpl.status === 'REJECTED' && <X className="size-3" />}
            {t(`templates.status.${tpl.status}`)}
          </Badge>
          {tpl.quality === 'RED' && (
            <Badge variant="destructive" title={t('templates.qualityRedHint')}>
              {t('templates.qualityRed')}
            </Badge>
          )}
          {isFallback && (
            <Badge variant="success" className="gap-1"><Star className="size-3" />{t('templates.isFallback')}</Badge>
          )}
          <span className="ml-auto flex gap-2">
            {tpl.status === 'APPROVED' && (
              <Button size="sm" variant="outline" onClick={onTest}>
                <Send className="size-3.5" />{t('templates.test.action')}
              </Button>
            )}
            {tpl.status === 'APPROVED' && !isFallback && (
              <Button size="sm" variant="outline" onClick={onFallback}>
                <Star className="size-3.5" />{t('templates.useAsFallback')}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onEdit} title={t('templates.edit.action')}>
              <Pencil className="size-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} title={t('templates.delete')}>
              <Trash2 className="size-3.5" />
            </Button>
          </span>
        </div>

        {tpl.spec?.header?.text && (
          <p className="text-sm font-medium text-muted-foreground">{tpl.spec.header.text}</p>
        )}
        <p className="whitespace-pre-wrap text-sm">{tpl.spec?.body?.text}</p>
        {tpl.spec?.footer && <p className="text-xs text-muted-foreground">{tpl.spec.footer}</p>}

        {/* Desempenho em uma linha: o suficiente para saber se o template
            funciona, sem abrir outra tela. */}
        {stats && stats.sent + stats.failed > 0 && (
          <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{t('templates.stats.period')}</span>
            <span><strong className="tabular-nums text-foreground">{stats.sent}</strong> {t('templates.stats.sent')}</span>
            {stats.sent > 0 && <>
              <span><strong className="tabular-nums text-foreground">{stats.delivery_rate.toFixed(1)}%</strong> {t('templates.stats.delivered')}</span>
              <span><strong className="tabular-nums text-foreground">{stats.read_rate.toFixed(1)}%</strong> {t('templates.stats.read')}</span>
            </>}
            {stats.failed > 0 && <span className="text-destructive"><strong className="tabular-nums">{stats.failed}</strong> {t('templates.stats.failed')}</span>}
          </p>
        )}

        {tpl.status === 'REJECTED' && tpl.rejected_reason && (
          <p className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            {t('templates.rejectedReason')}: {tpl.rejected_reason}
          </p>
        )}

        {/* Como enviar: o cliente nunca vê o id do template na Meta. */}
        <code className="overflow-x-auto rounded-md bg-muted/50 p-2 font-mono text-[11px] text-muted-foreground">
          {`POST /messages/template {"to":"+55…","template":"${tpl.name}"${
            tpl.variables > 0
              ? `,"variables":[${Array.from({ length: tpl.variables }, (_, i) => `"valor${i + 1}"`).join(',')}]`
              : ''
          }}`}
        </code>
      </CardContent>
    </Card>
  );
}

function CreateDialog({ open, onOpenChange, preset, editing }: {
  open: boolean; onOpenChange: (v: boolean) => void; preset?: TemplatePreset | null; editing?: Template | null;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('pt_BR');
  const [category, setCategory] = useState<Category>('UTILITY');
  const [body, setBody] = useState('');
  const [examples, setExamples] = useState<string[]>([]);
  const [footer, setFooter] = useState('');
  const [headerType, setHeaderType] = useState<HeaderType>('none');
  const [header, setHeader] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [headerMedia, setHeaderMedia] = useState('');
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [expiry, setExpiry] = useState(10);
  // Formatos ricos de marketing (ver TemplateRichFields).
  const [format, setFormat] = useState<TemplateFormat>('standard');
  const [offer, setOffer] = useState<OfferDraft>({ text: '', has_expiration: false });
  const [cardMedia, setCardMedia] = useState<CardMedia>('image');
  const [cardShape, setCardShape] = useState<CardButtonShape[]>([{ type: 'url', text: '' }]);
  const [cards, setCards] = useState<CardDraft[]>([emptyCard(), emptyCard()]);
  const [advice, setAdvice] = useState<string[]>([]);

  // Modelo pronto: preenche ao abrir (uma vez por abertura).
  useEffect(() => {
    if (!open || !preset) return;
    setName(preset.name); setCategory(preset.category);
    setBody(preset.body ?? ''); setExamples(preset.examples ?? []); setFooter(preset.footer ?? '');
    setButtons(preset.buttons ?? []);
  }, [open, preset]);

  // Edição: tudo o que o template tem volta para os campos.
  useEffect(() => {
    if (!open || !editing) return;
    const sp = editing.spec;
    setName(editing.name); setLanguage(editing.language); setCategory(editing.category);
    setBody(sp?.body?.text ?? ''); setExamples(sp?.body?.example ?? []); setFooter(sp?.footer ?? '');
    setButtons(sp?.buttons ?? []);
    setExpiry(sp?.code_expiry_minutes || 10);
    if (sp?.cards?.length) {
      const c = cardsFromSpec(sp.cards);
      setFormat('carousel'); setCardMedia(c.media); setCardShape(c.shape); setCards(c.cards);
    } else if (sp?.limited_time_offer) {
      setFormat('offer'); setOffer(sp.limited_time_offer);
    } else setFormat('standard');
    const h = sp?.header;
    if (!h) { setHeaderType('none'); setHeader(''); setHeaderExample(''); setHeaderMedia(''); }
    else if (h.type === 'text') { setHeaderType('text'); setHeader(h.text ?? ''); setHeaderExample(h.example ?? ''); }
    else { setHeaderType(h.type as HeaderType); setHeaderMedia(h.media_url ?? ''); }
  }, [open, editing]);
  // O aprovado não muda de categoria (regra da Meta).
  const categoryFixed = !!editing && editing.status === 'APPROVED';
  // Formato que a categoria não aceita volta ao padrão (oferta é só de marketing).
  useEffect(() => {
    if (format === 'offer' && category !== 'MARKETING') setFormat('standard');
    if (format === 'offer' && !['none', 'image', 'video'].includes(headerType)) setHeaderType('none');
    if (format === 'carousel' && category !== 'MARKETING' && category !== 'UTILITY') setFormat('standard');
  }, [format, category, headerType]);

  const parsed = parseVars(body);
  const vars = parsed.names.length;
  // Heurística de aviso, nunca de bloqueio: a Meta não publica o limite exato.
  const dense = vars >= 2 && body.trim().split(/\s+/).filter(Boolean).length < vars * 5;
  const headerVars = parseVars(header).names.length;
  const isAuth = category === 'AUTHENTICATION';
  // Categoria que a Meta ainda não liberou nesta conta não é escolha: enviar só volta
  // recusado. O porquê e o caminho estão no cartão Código de verificação.
  const { data: otpInfo } = useOTPInfo();
  const authLocked = !otpReleased(otpInfo?.eligibility);
  useEffect(() => {
    if (authLocked && category === 'AUTHENTICATION' && !editing) setCategory('UTILITY');
  }, [authLocked, category, editing]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const r = await apiFetch('POST', '/templates/media', fd);
      if (!r.ok) {
        const b = r.data as { message?: string };
        throw new Error(b?.message ?? t('templates.field.uploadFailed'));
      }
      return (r.data as { url: string }).url;
    },
    onSuccess: (url) => setHeaderMedia(url),
    onError: (e: Error) => toast.error(e.message),
  });

  // O template como vai para a API — o mesmo objeto na validação ao vivo e no envio.
  const buildSpec = (): Spec => (isAuth
        ? { name, language, category, body: { text: '' }, code_expiry_minutes: expiry }
        : format === 'carousel' ? {
            name, language, category,
            body: { text: body, example: examples.slice(0, vars) },
            cards: buildCards(cardMedia, cardShape, cards),
          }
        : {
            name, language, category,
            body: { text: body, example: examples.slice(0, vars) },
            ...(format === 'offer' ? { limited_time_offer: offer } : {}),
            ...(footer && format !== 'offer' ? { footer } : {}),
            ...(headerType === 'text' && header
              ? { header: { type: 'text', text: header, example: headerExample } }
              : {}),
            ...(headerType === 'location' ? { header: { type: 'location' } } : {}),
            ...(headerType !== 'text' && headerType !== 'none' && headerType !== 'location'
              ? { header: { type: headerType, media_url: headerMedia } }
              : {}),
            ...(buttons.length ? { buttons } : {}),
          });

  // Validação ao vivo, com as MESMAS regras do servidor (POST /templates/validate,
  // que não chama a Meta): o botão de enviar só libera o que a Meta aceitaria.
  // Cada recusa dela custa uma submissão e até 24 horas.
  const specKey = JSON.stringify(buildSpec());
  const [check, setCheck] = useState<{ valid: boolean; problem?: string } | null>(null);
  useEffect(() => {
    if (!open || !name || (!isAuth && !body.trim())) { setCheck(null); return; }
    let alive = true;
    const timer = setTimeout(async () => {
      const qs = editing ? `?id=${editing.id}` : '';
      const r = await apiFetch('POST', `/templates/validate${qs}`, JSON.parse(specKey));
      if (alive && r.ok) setCheck(r.data as { valid: boolean; problem?: string });
    }, 450);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, name, specKey, editing, isAuth, body]);
  const blocked = !!check && !check.valid;

  const create = useMutation({
    mutationFn: async () => {
      const spec = buildSpec();
      const r = editing
        ? await apiFetch('PUT', `/templates/${editing.id}`, spec)
        : await apiFetch('POST', '/templates', spec);
      if (!r.ok) {
        // A mensagem da validação É o produto: ela diz o que consertar.
        throw new Error((r.data as { message?: string })?.message ?? t('templates.createFailed'));
      }
      return r.data as { advice?: string[] };
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setAdvice(d.advice ?? []);
      toast.success(editing ? t('templates.edit.submitted') : t('templates.submitted'));
      if (!d.advice?.length) close();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function close() {
    onOpenChange(false);
    setName(''); setBody(''); setExamples([]); setFooter(''); setHeader('');
    setHeaderExample(''); setHeaderType('none'); setHeaderMedia('');
    setButtons([]); setAdvice([]);
    setFormat('standard'); setOffer({ text: '', has_expiration: false });
    setCardMedia('image'); setCardShape([{ type: 'url', text: '' }]); setCards([emptyCard(), emptyCard()]);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{editing ? t('templates.edit.title', { name: editing.name }) : t('templates.create.title')}</DialogTitle>
          <DialogDescription>{editing ? t('templates.edit.note') : t('templates.create.subtitle')}</DialogDescription>
        </DialogHeader>

        {/* Duas colunas a partir do desktop: a prévia fica À VISTA enquanto se
            escreve. Embaixo do formulário ela só apareceria depois de rolar tudo,
            que é justamente quando já não serve para corrigir o texto. */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="tpl-name">{t('templates.field.name')}</Label>
              <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!!editing}
                placeholder="boas_vindas" />
              <p className="mt-1 text-xs text-muted-foreground">{t('templates.field.nameHint')}</p>
            </div>
            <div>
              <Label htmlFor="tpl-lang">{t('templates.field.language')}</Label>
              <Input id="tpl-lang" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!!editing}
                placeholder="pt_BR" />
            </div>
          </div>

          <div>
            <Label>{t('templates.field.category')}</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const).map((c) => (
                <button key={c} type="button" onClick={() => setCategory(c)}
                  disabled={(c === 'AUTHENTICATION' && authLocked && !editing) || (categoryFixed && c !== category)}
                  className={
                    'rounded-full border px-3 py-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                    (category === c ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:text-foreground')
                  }>
                  {t(`templates.category.${c}`)}
                </button>
              ))}
            </div>
            {/* A categoria decide o PREÇO da conversa — não é rótulo administrativo. */}
            <p className="mt-1 text-xs text-muted-foreground">{t(`templates.categoryHint.${category}`)}</p>
            {authLocked && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t('templates.roles.otp.categoryLocked')}</p>}
          </div>

          {isAuth ? (
            <div>
              <Label htmlFor="tpl-exp">{t('templates.field.expiry')}</Label>
              <Input id="tpl-exp" type="number" min={1} max={90} value={expiry}
                onChange={(e) => setExpiry(Number(e.target.value))} />
              <p className="mt-1 text-xs text-muted-foreground">{t('templates.field.authHint')}</p>
            </div>
          ) : (
            <>
              <FormatPicker format={format} onChange={setFormat} category={category} />
              {format === 'offer' && <OfferFields value={offer} onChange={setOffer} />}
              {format !== 'carousel' && <div>
                <Label>{t('templates.field.header')}</Label>
                {/* A Meta aceita quatro formatos de cabeçalho; a tela só oferecia
                    texto, e os outros três já existiam no servidor sem porta. */}
                <div className="mt-1 flex flex-wrap gap-2">
                  {(format === 'offer'
                    ? (['none', 'image', 'video'] as const)
                    : (['none', 'text', 'image', 'video', 'document', 'location'] as const)).map((h) => (
                    <button key={h} type="button" onClick={() => setHeaderType(h)}
                      className={
                        'rounded-full border px-3 py-1 text-sm transition-colors ' +
                        (headerType === h ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:text-foreground')
                      }>
                      {t(`templates.header.${h}`)}
                    </button>
                  ))}
                </div>
                {headerType === 'text' && (
                  <>
                    <Input className="mt-2" value={header} onChange={(e) => setHeader(e.target.value)}
                      placeholder={t('templates.field.headerPlaceholder')} maxLength={60} />
                    {headerVars > 1 && (
                      <p className="mt-1 text-xs text-destructive">{t('templates.field.headerOneVar')}</p>
                    )}
                    {headerVars === 1 && (
                      <Input className="mt-2" value={headerExample} onChange={(e) => setHeaderExample(e.target.value)}
                        placeholder={t('templates.field.headerExample')} />
                    )}
                  </>
                )}
                {headerType === 'location' && (
                  <p className="mt-2 text-xs text-muted-foreground">{t('templates.field.locationHint')}</p>
                )}
                {headerType !== 'none' && headerType !== 'text' && headerType !== 'location' && (
                  <div className="mt-2 flex flex-col gap-2">
                    {/* Antes era colar um link. O cliente precisava hospedar o
                        arquivo em algum lugar — passo fora do produto, que ele
                        não sabe fazer e que quebra quando ele apaga o arquivo. */}
                    <label className="inline-flex w-fit">
                      <input type="file" className="hidden"
                        accept={headerType === 'image' ? 'image/jpeg,image/png'
                          : headerType === 'video' ? 'video/mp4' : 'application/pdf'}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (f) upload.mutate(f);
                        }} />
                      <span className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted">
                        {upload.isPending && <Loader2 className="size-4 animate-spin" />}
                        {headerMedia ? t('templates.field.mediaReplace') : t('templates.field.mediaUpload')}
                      </span>
                    </label>
                    {headerMedia && (
                      <p className="truncate font-mono text-xs text-muted-foreground">{headerMedia}</p>
                    )}
                    <p className="text-xs text-muted-foreground">{t('templates.field.mediaHint')}</p>
                  </div>
                )}
              </div>}

              <div>
                <Label htmlFor="tpl-body">{t('templates.field.body')}</Label>
                <textarea id="tpl-body" className={taClass} rows={4} value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('templates.field.bodyPlaceholder')} />
                <p className="mt-1 text-xs text-muted-foreground">{t('templates.field.bodyHint')}</p>
              </div>

              {parsed.mixed && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {t('templates.field.mixedVars')}
                </p>
              )}

              {/* Densidade de variáveis: a Meta recusa por isso, e a recusa
                  custa uma submissão. O aviso tem de aparecer ENQUANTO se
                  escreve — depois do envio já é tarde. */}
              {dense && (
                <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
                  {t('templates.field.denseBody')}
                </p>
              )}

              {vars > 0 && !parsed.mixed && (
                <div>
                  <Label>{t('templates.field.examples')}</Label>
                  <p className="mb-2 text-xs text-muted-foreground">{t('templates.field.examplesHint')}</p>
                  <div className="flex flex-col gap-2">
                    {/* Cada campo é rotulado pela SUA variável. Com nomes, uma
                        lista de caixas iguais obrigaria a contar posições. */}
                    {parsed.names.map((n, i) => (
                      <div key={n} className="flex items-center gap-2">
                        <code className="min-w-24 shrink-0 rounded bg-muted px-2 py-1 text-xs">{`{{${n}}}`}</code>
                        <Input value={examples[i] ?? ''}
                          onChange={(e) => {
                            const next = [...examples];
                            next[i] = e.target.value;
                            setExamples(next);
                          }}
                          placeholder={t('templates.field.examplePlaceholder')} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {format === 'standard' && (
                <div>
                  <Label htmlFor="tpl-footer">{t('templates.field.footer')}</Label>
                  <Input id="tpl-footer" value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={60} />
                </div>
              )}

              {format === 'carousel'
                ? <CarouselEditor media={cardMedia} setMedia={setCardMedia} shape={cardShape} setShape={setCardShape}
                    cards={cards} setCards={setCards} />
                : <ButtonBuilder buttons={buttons} onChange={setButtons} category={category} />}
            </>
          )}

          {advice.length > 0 && (
            <div className="rounded-md bg-amber-500/10 p-3 text-sm">
              {advice.map((a) => (
                <p key={a} className="flex gap-2"><TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />{a}</p>
              ))}
            </div>
          )}

          {blocked && check?.problem && (
            <p className="flex gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive" role="alert">
              <CircleAlert className="mt-0.5 size-4 shrink-0" />
              <span>{t('templates.create.metaWouldReject')} {check.problem}</span>
            </p>
          )}

          <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            {t('templates.create.reviewNote')}
          </p>
        </div>

        {!isAuth && (
          // `sticky` para a prévia não sumir quando o formulário é longo: com
          // botões e exemplos ele passa de uma tela, e ver o resultado enquanto
          // se escreve é o ponto todo.
          <div className="lg:sticky lg:top-0 lg:self-start">
            {format === 'carousel'
              ? <CarouselPreview body={body} media={cardMedia} shape={cardShape} cards={cards} />
              : <Preview headerType={headerType} headerText={header} media={headerMedia}
                  body={body} footer={format === 'offer' ? '' : footer} buttons={buttons} names={parsed.names}
                  examples={examples} offer={format === 'offer' ? offer.text : undefined} />}
          </div>
        )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common.cancel')}</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !name || (!isAuth && !body) || blocked}>
            {create.isPending && <Loader2 className="size-4 animate-spin" />}
            {advice.length > 0 ? t('common.close') : t('templates.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type HeaderType = 'none' | 'text' | 'image' | 'video' | 'document' | 'location';

/** Os tipos de botão da Cloud API, com os limites que a Meta impõe a cada um. */
const BUTTON_KINDS = [
  { type: 'quick_reply', max: 10 },
  { type: 'url', max: 2 },
  { type: 'phone_number', max: 1 },
  { type: 'copy_code', max: 1 },
] as const;

/**
 * Construtor de botões.
 *
 * Existiam no servidor desde sempre e nunca tiveram porta na tela — o cliente
 * que quisesse um botão tinha de ir ao WhatsApp Manager, que é justamente o que
 * o produto promete evitar.
 *
 * Os limites da Meta são aplicados AQUI, desabilitando o que já estourou, em vez
 * de deixar montar e recusar depois: cada recusa dela custa uma submissão, e
 * submissões são poucas.
 */
function ButtonBuilder({ buttons, onChange, category }: {
  buttons: TemplateButton[];
  onChange: (b: TemplateButton[]) => void;
  category: Category;
}) {
  const t = useT();
  const count = (k: string) => buttons.filter((b) => b.type === k).length;
  const set = (i: number, patch: Partial<TemplateButton>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('templates.buttons.title')}</Label>
      <p className="text-xs text-muted-foreground">{t('templates.buttons.hint')}</p>

      {buttons.map((b, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
              {t(`templates.buttons.kind.${b.type}`)}
            </span>
            <Input className="flex-1" value={b.text} maxLength={25}
              placeholder={t('templates.buttons.label')}
              onChange={(e) => set(i, { text: e.target.value })} />
            <Button variant="ghost" size="sm"
              onClick={() => onChange(buttons.filter((_, j) => j !== i))}>
              {t('templates.buttons.remove')}
            </Button>
          </div>

          {b.type === 'url' && (
            <>
              <Input value={b.url ?? ''} placeholder="https://exemplo.com/pedido/{{1}}"
                onChange={(e) => set(i, { url: e.target.value })} />
              {/* URL com variável exige exemplo: a Meta precisa ver o link
                  montado para aprovar. */}
              {(b.url ?? '').includes('{{') && (
                <Input value={b.example ?? ''} placeholder={t('templates.buttons.urlExample')}
                  onChange={(e) => set(i, { example: e.target.value })} />
              )}
            </>
          )}
          {b.type === 'phone_number' && (
            <Input value={b.phone ?? ''} placeholder="+5551999999999"
              onChange={(e) => set(i, { phone: e.target.value })} />
          )}
          {b.type === 'copy_code' && (
            <Input value={b.example ?? ''} placeholder={t('templates.buttons.codeExample')}
              onChange={(e) => set(i, { example: e.target.value })} />
          )}
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        {BUTTON_KINDS.map(({ type, max }) => (
          <Button key={type} variant="outline" size="sm"
            disabled={count(type) >= max || buttons.length >= 10 || (type === 'copy_code' && category !== 'MARKETING')}
            title={type === 'copy_code' && category !== 'MARKETING' ? t('templates.buttons.copyMarketingOnly') : undefined}
            onClick={() => onChange([...buttons, { type, text: '' }])}>
            <Plus className="size-3.5" /> {t(`templates.buttons.kind.${type}`)}
          </Button>
        ))}
      </div>
      {category !== 'MARKETING' && buttons.some((b) => b.type === 'copy_code') && (
        <p className="mt-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          {t('templates.buttons.copyMarketingOnly')}
        </p>
      )}
    </div>
  );
}

/**
 * Pré-visualização — o template como ele chega no celular.
 *
 * As variáveis aparecem SUBSTITUÍDAS pelos exemplos, porque é assim que o
 * cliente final vai ler. Ver "Olá {{nome}}" não ajuda ninguém a julgar se o
 * texto ficou bom; ver "Olá Vinicius Berni" ajuda — e é também o que a Meta
 * analisa.
 *
 * Enquanto falta o exemplo, mostra o nome da variável em destaque: é um lembrete
 * do que ainda não foi preenchido, no lugar exato onde vai aparecer.
 */
function Preview({ headerType, headerText, media, body, footer, buttons, names, examples, offer }: {
  offer?: string;
  headerType: HeaderType; headerText: string; media: string; body: string;
  footer: string; buttons: TemplateButton[]; names: string[]; examples: string[];
}) {
  const t = useT();
  const fill = (text: string) =>
    text.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, v) => {
      const i = names.indexOf(v);
      const ex = i >= 0 ? examples[i] : '';
      return ex?.trim() ? ex : `{{${v}}}`;
    });

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('templates.preview.title')}</Label>
      {/* O verde do WhatsApp, não a paleta do bZapper: o valor da prévia está em
          parecer com o destino, não com a nossa interface. */}
      <div className="rounded-lg bg-[#e5ddd5] p-4 dark:bg-[#0b141a]">
        <div className="max-w-sm rounded-lg rounded-tl-none bg-white p-2 shadow-sm dark:bg-[#202c33]">
          {headerType === 'image' && (
            media ? <img src={media} alt="" className="mb-2 max-h-40 w-full rounded object-cover" />
              : <div className="mb-2 flex h-24 items-center justify-center rounded bg-black/5 text-xs text-muted-foreground dark:bg-white/5">{t('templates.preview.noMedia')}</div>
          )}
          {headerType === 'video' && (
            <div className="mb-2 flex h-24 items-center justify-center rounded bg-black/5 text-xs text-muted-foreground dark:bg-white/5">
              {media ? t('templates.preview.video') : t('templates.preview.noMedia')}
            </div>
          )}
          {headerType === 'document' && (
            <div className="mb-2 flex items-center gap-2 rounded bg-black/5 p-2 text-xs dark:bg-white/5">
              <FileText className="size-4 shrink-0" />
              {media ? t('templates.preview.document') : t('templates.preview.noMedia')}
            </div>
          )}
          {headerType === 'text' && headerText && (
            <p className="mb-1 text-sm font-semibold">{fill(headerText)}</p>
          )}
          {offer && (
            <p className="mb-1 inline-flex items-center gap-1 rounded bg-rose-500/10 px-2 py-0.5 text-xs font-semibold text-rose-600">⏱ {offer}</p>
          )}
          {headerType === 'location' && (
            <div className="mb-2 flex h-24 items-center justify-center gap-1.5 rounded bg-black/5 text-xs text-muted-foreground dark:bg-white/5">
              <MapPin className="size-4" />{t('templates.preview.location')}
            </div>
          )}

          <p className="whitespace-pre-wrap break-words text-sm">
            {fill(body) || <span className="text-muted-foreground">{t('templates.preview.emptyBody')}</span>}
          </p>

          {footer && <p className="mt-1 text-[11px] text-muted-foreground">{footer}</p>}

          {buttons.length > 0 && (
            <div className="mt-2 flex flex-col gap-px border-t pt-1">
              {/* A Meta mostra no máximo dois e agrupa o resto — a prévia mente
                  menos repetindo esse comportamento que listando os dez. */}
              {buttons.slice(0, 2).map((b, i) => (
                <span key={i} className="py-1 text-center text-sm text-[#00a5f4]">
                  {b.text || t('templates.buttons.label')}
                </span>
              ))}
              {buttons.length > 2 && (
                <span className="py-1 text-center text-sm text-[#00a5f4]">
                  {t('templates.preview.moreButtons')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
